import React, { useState, useEffect, useContext } from "react";
import { _FilterNetworkSystemsFC } from "../helpers/_FilterNetworkSystemsFC";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import { SystemListFC } from "./SystemListFC";
import { InputContext } from "../../_context/InputContext";
import {
  Loader,
  GlassCard,
  SectionTitle,
  NoDataState,
  SpacedGlassCard,
} from "../../../../ui";

export const _NetworkFC = (props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [systems, setSystems] = useState();
  const [filteredSystems, setFilteredSystems] = useState();
  const [displaySystems, setDisplaySystems] = useState();
  const [roles, setRoles] = useState();
  const [loadingSystemData, setLoadingSystemData] = useState(false);
  const [error, setError] = useState(null);
  const { adminCustomerToggle, setAdminCustomerToggle } =
    useContext(InputContext);

  const getSystemData = async () => {
    setLoadingSystemData(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/systems`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setSystems(res?.data);
    } catch (error) {
      console.error("Failed to fetch system data:", error);
      setError("Failed to load system data. Please try again.");
    }

    setLoadingSystemData(false);
  };

  const filterSystems = () => {
    if (adminCustomerToggle && systems) {
      setFilteredSystems(
        systems[0]?.filter(
          (system) => system.customer_id === adminCustomerToggle,
        ),
      );
      setDisplaySystems(
        systems[0]?.filter(
          (system) => system.customer_id === adminCustomerToggle,
        ),
      );
    } else if (systems) {
      setFilteredSystems(systems[0]);
      setDisplaySystems(systems[0]);
    }
  };

  useEffect(() => {
    getSystemData();
    setRoles(props.userRoles);
  }, []);

  useEffect(() => {
    filterSystems();
  }, [systems, adminCustomerToggle]);

  return (
    <NetworkContainer>
      <SectionTitle
        title="Network Systems"
        subtitle="Manage and monitor system configurations"
      />

      {loadingSystemData ? (
        <LoaderContainer>
          <Loader size="large" message="Loading system data..." />
        </LoaderContainer>
      ) : error ? (
        <ErrorContainer>
          <NoDataState
            title="Error Loading Systems"
            description={error}
            showRetry
            onRetry={getSystemData}
          />
        </ErrorContainer>
      ) : !filteredSystems || filteredSystems.length === 0 ? (
        <EmptyContainer>
          <NoDataState
            title="No Systems Available"
            description="No network systems are available for this customer"
          />
        </EmptyContainer>
      ) : (
        <ContentContainer>
          <FilterContainer>
            <SpacedGlassCard>
              <_FilterNetworkSystemsFC
                systems={filteredSystems}
                displaySystems={filteredSystems}
                setDisplaySystems={setDisplaySystems}
              />
            </SpacedGlassCard>
          </FilterContainer>

          <SystemsContainer>
            {!displaySystems || displaySystems.length === 0 ? (
              <EmptyResultsContainer>
                <SpacedGlassCard>
                  <NoDataState
                    title="No Results Found"
                    description="No systems match your search or filter criteria. Try adjusting your filters or search term."
                  />
                </SpacedGlassCard>
              </EmptyResultsContainer>
            ) : (
              <SystemListFC
                systems={displaySystems}
                acuamticaSystems={systems?.[1]}
              />
            )}
          </SystemsContainer>
        </ContentContainer>
      )}
    </NetworkContainer>
  );
};

// Styled Components
const NetworkContainer = styled.div`
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
  min-height: 100vh;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FilterContainer = styled.div`
  width: 100%;
`;

const SystemsContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const EmptyResultsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;
